import { Check, Copy } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/**
 * Copy `text` to the clipboard, and say so on the button for a moment.
 *
 * `text` may be a function, for a value that should not be computed until the click --
 * a secret that is masked on screen, say, so what is copied is not what is shown.
 */
export function CopyButton({
  text,
  label = "Copy",
  copiedLabel = "Copied",
  size = "xs",
  variant = "outline",
  iconOnly = false,
  className,
}: {
  text: string | (() => string);
  label?: string;
  copiedLabel?: string;
  size?: "xs" | "sm" | "icon-xs" | "icon-sm";
  variant?: "outline" | "ghost";
  iconOnly?: boolean;
  className?: string;
}) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(typeof text === "function" ? text() : text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy — select the text manually");
    }
  }

  return (
    <Button
      type="button"
      size={size}
      variant={variant}
      onClick={copy}
      className={cn("shrink-0", className)}
      aria-label={iconOnly ? (copied ? copiedLabel : label) : undefined}
    >
      {copied ? <Check className="size-3.5" aria-hidden /> : <Copy className="size-3.5" aria-hidden />}
      {iconOnly ? null : copied ? copiedLabel : label}
    </Button>
  );
}
